import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query';

import { ErrorResponse, Spinner } from '~components';
import { faEdit } from '~components/icon';
import { TableView } from '~components/table-view';
import { linkCellRender } from '~components/table-view/columnRenderers';
import { Column, Row } from '~components/table-view/types';

import { getErrorMessage } from '~libs/axiosWrapper';

import { InputProps } from '~components/form-fields/props';

import { useGetLevelMovePreviewQuery } from '~support-at-home/actions/packageSchedules';

const openAppointmentInNewTab = (row: Row) => {
  window.open(`/appointments/${row.id}`, '_blank', 'noopener,noreferrer');
};
const renderIdAsLink = linkCellRender(openAppointmentInNewTab, faEdit);

const renderServiceTypeName = (_value: any, row: Row) => row.service_type?.name ?? '';
const renderUserFullName = (_value: any, row: Row) => row.user?.full_name ?? '';

const repeatsColumns: Column[] = [
  { id: 'id', header: 'ID', type: 'readonly', renderReadOnly: renderIdAsLink },
  { id: 'repeat_weekday', header: 'Weekday', type: 'readonly' },
  { id: 'repeat_start', header: 'Repeat Start', type: 'readonly' },
  { id: 'repeat_end', header: 'Repeat End', type: 'readonly' },
  { id: 'start_time', header: 'Start Time', type: 'readonly' },
  { id: 'end_time', header: 'End Time', type: 'readonly' },
  { id: 'service_type', header: 'Service Type', type: 'readonly', renderReadOnly: renderServiceTypeName },
  { id: 'user', header: 'Carer', type: 'readonly', renderReadOnly: renderUserFullName },
];

const fixedColumns: Column[] = [
  { id: 'id', header: 'ID', type: 'readonly', renderReadOnly: renderIdAsLink },
  { id: 'date', header: 'Date', type: 'readonly' },
  { id: 'start_time', header: 'Start Time', type: 'readonly' },
  { id: 'end_time', header: 'End Time', type: 'readonly' },
  { id: 'service_type', header: 'Service Type', type: 'readonly', renderReadOnly: renderServiceTypeName },
  { id: 'user', header: 'Carer', type: 'readonly', renderReadOnly: renderUserFullName },
];

type ComponentProps = InputProps;

const Preview: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { scheduleId: scheduleIdRaw } = useParams<{ scheduleId: string }>();
  const scheduleId = Number(scheduleIdRaw);

  const formValues = value as Record<string, any>;
  const changeoverDate: string = formValues?.changeover_date ?? '';

  const { data, isLoading, error, isError } = useGetLevelMovePreviewQuery(
    changeoverDate && scheduleId ? { scheduleId, changeoverDate } : skipToken,
  );

  const [selectedRepeats, setSelectedRepeats] = useState<string[]>([]);
  const [selectedFixed, setSelectedFixed] = useState<string[]>([]);

  useEffect(() => {
    if (data) {
      setSelectedRepeats(data.repeats.map((appt) => String(appt.id)));
      setSelectedFixed(data.fixed.map((appt) => String(appt.id)));
    }
  }, [data]);

  useEffect(() => {
    if (onChange) onChange('repeat_appointment_ids', selectedRepeats.map(Number));
  }, [selectedRepeats]);

  useEffect(() => {
    if (onChange) onChange('fixed_appointment_ids', selectedFixed.map(Number));
  }, [selectedFixed]);

  if (!changeoverDate) {
    return <p className="text-muted">Please select a changeover date in Step 1.</p>;
  }

  if (isLoading) {
    return <Spinner loading={isLoading} />;
  }

  if (isError) {
    return <ErrorResponse message={getErrorMessage(error)} />;
  }

  if (!data) return null;

  return (
    <div>
      <Alert color="info" className="mb-3">
        Please select all appointments you want to switch to the new package.
      </Alert>
      <h6 className="mb-3">Repeating Appointments</h6>
      <TableView
        id="level-move-repeats-table"
        columns={repeatsColumns}
        data={data.repeats}
        isLoading={isLoading}
        emptyMessage="No repeating appointments found"
        canSelect
        selectedIds={selectedRepeats}
        onRowSelectionChange={setSelectedRepeats}
        readonly
        striped
        bordered
      />

      <h6 className="mt-4 mb-3">Single Appointments</h6>
      <TableView
        id="level-move-fixed-table"
        columns={fixedColumns}
        data={data.fixed}
        isLoading={isLoading}
        emptyMessage="No single appointments found"
        canSelect
        selectedIds={selectedFixed}
        onRowSelectionChange={setSelectedFixed}
        readonly
        striped
        bordered
      />
    </div>
  );
};

export default Preview;
